import { ReactNode, useEffect, useRef } from 'react'

import { ScheduleSteps } from 'src/Types/ui'

const BACK_INDEX = -1

interface UseStepSyncParams {
  search: URLSearchParams
  setSearch: (params: Record<string, string>) => void
  mapSteps: Record<string, ReactNode>
  defaultStep: ScheduleSteps
}

const getIsFromPaymentPage = (search: URLSearchParams): boolean => {
  const paypalEmail = search.get('paypal_email')
  const paymentStatus = search.get('paymentStatus')
  return Boolean(paypalEmail || paymentStatus)
}

const getShouldRedirectToDefault = (
  currentStepIndex: number,
  defaultStepIndex: number,
  prevDefaultStepIndex: number,
  isInitialMount: boolean,
): boolean => {
  const areAllStepIndicesValid =
    currentStepIndex !== BACK_INDEX && prevDefaultStepIndex !== BACK_INDEX

  const hasDefaultStepMovedForward =
    areAllStepIndicesValid && defaultStepIndex > prevDefaultStepIndex

  const hasDefaultStepMovedBackward =
    areAllStepIndicesValid && defaultStepIndex < prevDefaultStepIndex

  const isStepBehindAfterForwardMove =
    hasDefaultStepMovedForward && currentStepIndex < defaultStepIndex

  const isStepAheadAfterBackwardMove =
    hasDefaultStepMovedBackward && currentStepIndex > defaultStepIndex

  const shouldRedirectFromInitialStep =
    isInitialMount && currentStepIndex > defaultStepIndex

  return (
    isStepBehindAfterForwardMove ||
    isStepAheadAfterBackwardMove ||
    shouldRedirectFromInitialStep
  )
}

export const useStepSync = ({
  search,
  setSearch,
  mapSteps,
  defaultStep,
}: UseStepSyncParams): void => {
  const prevDefaultStepRef = useRef(defaultStep)
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    const step = search.get('step')
    const isFromPaymentPage = getIsFromPaymentPage(search)

    const isStepValid = Object.keys(mapSteps).includes(step as ScheduleSteps)

    if ((!step || !isStepValid) && !isFromPaymentPage) {
      setSearch({ step: defaultStep })
      prevDefaultStepRef.current = defaultStep
      isInitialMountRef.current = false
      return
    }

    const allSteps = Object.keys(mapSteps) as ScheduleSteps[]
    const currentStepIndex = allSteps.indexOf(step as ScheduleSteps)
    const defaultStepIndex = allSteps.indexOf(defaultStep)
    const prevDefaultStepIndex = allSteps.indexOf(prevDefaultStepRef.current)

    const shouldRedirect = getShouldRedirectToDefault(
      currentStepIndex,
      defaultStepIndex,
      prevDefaultStepIndex,
      isInitialMountRef.current,
    )

    if (shouldRedirect && !isFromPaymentPage) {
      setSearch({ step: defaultStep })
    }

    isInitialMountRef.current = false
    prevDefaultStepRef.current = defaultStep
  }, [search, setSearch, mapSteps, defaultStep])
}
